import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowRight, Sun, Moon, ArrowLeft } from 'lucide-react';

export default function OfficerRegistration() {
    const { registerOfficer, isAuthenticated, user } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [employmentId, setEmploymentId] = useState('');
    const [divisionArea, setDivisionArea] = useState('');

    // Redirect already-authenticated users to their dashboard
    React.useEffect(() => {
        if (isAuthenticated && user) {
            if (user.role === 'admin') navigate('/admin');
            else if (user.role === 'officer') navigate('/officer');
            else if (user.role === 'officer_pending' || user.role === 'officer_rejected') navigate('/pending-approval');
            else navigate('/dashboard');
        }
    }, [isAuthenticated, user, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!employmentId || isNaN(Number(employmentId)) || Number(employmentId) < 1) {
            toast.error('Employment ID must be a positive number');
            return;
        }
        setIsLoading(true);
        try {
            await registerOfficer(
                name,
                email,
                password,
                phone || null,
                parseInt(employmentId, 10),
                divisionArea
            );
            toast.success('Application submitted! Awaiting admin approval.');
            navigate('/pending-approval');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Theme Toggle */}
            <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="fixed top-4 right-4 z-50"
            >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <div className="flex-1 flex items-center justify-center p-4">
                <div className="w-full max-w-md space-y-4">
                    {/* Back link */}
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to main site
                    </Link>

                    <Card className="shadow-xl">
                        <CardHeader className="text-center">
                            <div className="flex justify-center mb-3">
                                <img
                                    src="/logo.png"
                                    alt="TrafficWatch logo"
                                    className="h-14 w-auto rounded-xl"
                                />
                            </div>
                            <CardTitle className="text-2xl">Officer Registration</CardTitle>
                            <CardDescription>
                                Submit your details for admin review. Access is granted after approval.
                            </CardDescription>
                        </CardHeader>

                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full Name</Label>
                                    <Input
                                        id="name"
                                        type="text"
                                        placeholder="Officer John Doe"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        data-testid="officer-name-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email">Official Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="officer@police.gov.in"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        data-testid="officer-email-input"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <Label htmlFor="employment-id">Employment ID</Label>
                                        <Input
                                            id="employment-id"
                                            type="number"
                                            placeholder="e.g. 100234"
                                            value={employmentId}
                                            onChange={(e) => setEmploymentId(e.target.value)}
                                            required
                                            min={1}
                                            data-testid="officer-employment-id-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Phone (Optional)</Label>
                                        <Input
                                            id="phone"
                                            type="tel"
                                            placeholder="+91 98765 43210"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            data-testid="officer-phone-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="division-area">Division Area</Label>
                                    <Input
                                        id="division-area"
                                        type="text"
                                        placeholder="e.g. South Mumbai Division"
                                        value={divisionArea}
                                        onChange={(e) => setDivisionArea(e.target.value)}
                                        required
                                        data-testid="officer-division-area-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password">Password</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        data-testid="officer-password-input"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={isLoading}
                                    data-testid="officer-register-submit-btn"
                                >
                                    {isLoading ? 'Submitting...' : 'Submit Application'}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <footer className="py-4 text-center text-xs text-muted-foreground border-t">
                <div className="flex items-center justify-center gap-2">
                    <img src="/logo.png" alt="" className="h-5 w-auto rounded" />
                    <span>TrafficWatch — Officer Portal</span>
                </div>
            </footer>
        </div>
    );
}
